import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Pencil, Loader2, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import api, { apiError } from "@/lib/api";
import { Badge } from "@/components/kit";
import { initials } from "@/components/shell/AppShell";

const ROLE_STYLES = {
  super_admin: { label: "Super Admin", tone: "bad" },
  admin: { label: "Admin", tone: "warn" },
  user: { label: "User", tone: "good" },
};

const inputCls = "mk-input";

export const UsersRolesPanel = () => {
  const { user: me } = useAuth();
  const { tenants } = useTenant();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "user", tenant_ids: [] });
  const [busy, setBusy] = useState(false);

  const isSuperAdmin = me?.role === "super_admin";
  const tName = (id) => tenants.find((t) => t.id === id)?.name || "?";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm({ email: "", name: "", role: "user", tenant_ids: [] }); setDialogOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ email: u.email, name: u.name, role: u.role, tenant_ids: u.tenant_ids || [] }); setDialogOpen(true); };
  const toggleTenant = (id) => setForm({ ...form, tenant_ids: form.tenant_ids.includes(id) ? form.tenant_ids.filter((x) => x !== id) : [...form.tenant_ids, id] });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, { name: form.name, role: form.role, tenant_ids: form.tenant_ids });
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User added to whitelist");
      }
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.name} removed`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const canManage = (u) => !(me?.role === "admin" && u.role === "super_admin") && u.id !== me?.id;

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-thstrong">Users & Roles</h2>
          <p className="mt-1 text-sm text-thmute">Only whitelisted emails can request an access code. Assign clients to give users tenant access.</p>
        </div>
        <button data-testid="add-user-btn" onClick={openAdd}
          className="mk-btn-primary">
          <UserPlus className="h-4 w-4" /> Add User
        </button>
      </div>

      <div className="mk-card overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-16 text-thcyan"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <table className="mk-table" data-testid="users-table">
            <thead><tr>
              {["Name", "Email", "Role", "Clients", "Last Login", ""].map((h, i) => (
                <th key={i} className={i === 0 ? "pl-5" : ""}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {users.map((u) => {
                const rs = ROLE_STYLES[u.role] || ROLE_STYLES.user;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.email}`}>
                    <td className="pl-5 text-sm font-semibold text-thstrong">
                      <span className="flex items-center gap-3">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-thblue/15 font-mono-hud text-[10px] font-semibold text-thblue">{initials(u.name)}</span>
                        {u.name} {u.id === me?.id && <span className="font-mono-hud text-[10px] text-thcyan">(you)</span>}
                      </span>
                    </td>
                    <td className="font-mono-hud text-xs text-thmute">{u.email}</td>
                    <td><Badge tone={rs.tone}>{rs.label}</Badge></td>
                    <td className="text-xs text-thbody">
                      {["admin", "super_admin"].includes(u.role) ? <span className="text-thdim">All clients</span>
                        : (u.tenant_ids || []).length ? u.tenant_ids.map(tName).join(", ") : <span className="text-thdim">None</span>}
                    </td>
                    <td className="font-mono-hud text-xs text-thdim">{u.last_login ? new Date(u.last_login).toLocaleString() : "Never"}</td>
                    <td className="pr-5">
                      {canManage(u) ? (
                        <div className="flex items-center justify-end gap-3">
                          <button data-testid={`edit-user-${u.email}`} onClick={() => openEdit(u)} className="text-thmute hover:text-thcyan"><Pencil className="h-4 w-4" /></button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button data-testid={`delete-user-${u.email}`} className="text-thmute hover:text-thcoral"><Trash2 className="h-4 w-4" /></button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="border-thborder/12 bg-thsolid rounded-2xl text-thstrong">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove {u.name}?</AlertDialogTitle>
                                <AlertDialogDescription className="text-thmute">{u.email} will lose access to Mieruka immediately.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="border-thborder/10 bg-transparent text-thbody hover:bg-thhover/10 hover:text-thstrong">Cancel</AlertDialogCancel>
                                <AlertDialogAction data-testid="confirm-delete-btn" onClick={() => remove(u)} className="rounded-full bg-thcoral text-white hover:bg-thcoral/85">Remove</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      ) : (
                        u.id !== me?.id && (
                          <span className="flex items-center justify-end gap-1.5 font-mono-hud text-[10px] uppercase tracking-[0.12em] text-thfaint">
                            <ShieldAlert className="h-3.5 w-3.5" /> Protected
                          </span>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="border-thborder/12 bg-thsolid rounded-2xl text-thstrong sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit user" : "Add user to whitelist"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="mt-2 flex flex-col gap-5">
            <div>
              <label className="font-mono-hud mb-2 block text-[10px] uppercase tracking-[0.25em] text-thmute">Email</label>
              <input data-testid="user-form-email" type="email" required disabled={!!editing} value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="agent@company.com" className={`${inputCls} disabled:opacity-50`} />
            </div>
            <div>
              <label className="font-mono-hud mb-2 block text-[10px] uppercase tracking-[0.25em] text-thmute">Name</label>
              <input data-testid="user-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className={inputCls} />
            </div>
            <div>
              <label className="font-mono-hud mb-2 block text-[10px] uppercase tracking-[0.25em] text-thmute">Role</label>
              <select data-testid="user-form-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
                {isSuperAdmin && <option value="super_admin">Super Admin</option>}
              </select>
            </div>
            {form.role === "user" && (
              <div>
                <label className="font-mono-hud mb-2 block text-[10px] uppercase tracking-[0.25em] text-thmute">Client access</label>
                <div className="flex max-h-36 flex-col gap-1.5 overflow-y-auto rounded-md border border-thborder/10 p-3">
                  {tenants.length === 0 && <p className="text-xs text-thdim">No clients yet — create one in Settings → Clients.</p>}
                  {tenants.map((t) => (
                    <label key={t.id} className="flex cursor-pointer items-center gap-2 text-sm text-thbody">
                      <input data-testid={`user-form-tenant-${t.name}`} type="checkbox" checked={form.tenant_ids.includes(t.id)} onChange={() => toggleTenant(t.id)} className="h-4 w-4 accent-[rgb(var(--th-cyan))]" />
                      {t.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <button data-testid="user-form-submit" type="submit" disabled={busy}
              className="mk-btn-primary mt-1 justify-center !py-3">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Add user"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
